import { PagesRouter, sanitizeSiteTitle, useLogger } from '@omnia/ui/src/lib'
import { cmsClient } from '@omnia/ui/src/lib/lumos-plus/domain/cms-client'
import { loadCmsData } from '@omnia/ui/src/lib/lumos-plus/domain/utils/load-cms-data'
import { setAssets } from '@omnia/ui/src/lib/lumos-plus/domain/utils/set-assets'
import { setTheme } from '@omnia/ui/src/lib/lumos-plus/domain/utils/set-theme'
import { getOccContextProps } from '@omnia/ui/src/lib/lumos-plus/utils/get-occ-context-props'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SalesChannel } from '@vista-digital/lumos-cms-client/src/lib/types/sales-channel'
import { deepMerge } from '@vista-digital/occ-domain/src/lib/utils/object-utils'
import { defaultOccContextProps, OccContextComponent } from '@vista-digital/occ-ui'
import type { FC } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useAsync } from 'react-async'
import { Helmet } from 'react-helmet'
import { cmsDataService } from './common/services/cms.data.service'
import { Layout } from './features/layout/Layout'

interface AppOverrideProps {
    pagesRouterOverride?: FC
}

export const App: FC<AppOverrideProps> = props => {
    const logger = useLogger('App')

    const [queryClient] = useState(() => new QueryClient())

    logger.info('App rendering', { loadTime: Date.now() - window.loadMs, version: APP_VERSION })

    const PagesRouterElement = useMemo(() => props.pagesRouterOverride ?? PagesRouter, [props.pagesRouterOverride])

    cmsClient.update({
        salesChannel: SalesChannel.Web,
        apiUrl: cmsDataService.get().lumos.cmsApiUrl.replace(/\/$/, ''), // Remove trailing slash
    })

    const { isPending } = useAsync(loadCmsData)

    useEffect(() => {
        if (isPending) {
            return
        }

        logger.debug('Setting lumos theme and assets')

        setTheme()
        setAssets()
    }, [isPending, logger])

    const occContextProps = useMemo(() => {
        const occCmsProps = getOccContextProps(cmsDataService.get().api.authToken) ?? {}

        // Lumos configuration is currently missing number locale, but does has currency which inherits from number.
        // Check that we do have currency config, and do not have number config, and use currency config as number config.
        // If we do have number config, then this bug has been fixed in Lumos, and we should skip this part.
        if (occCmsProps.config?.locale?.currency && !occCmsProps.config.locale.number) {
            occCmsProps.config.locale.number = occCmsProps.config.locale.currency
        }

        const occContextMergedProps = deepMerge(defaultOccContextProps, occCmsProps)

        logger.debug('Created OCC Context Props from Lumos configuration', { occCmsProps, occContextMergedProps })

        return occContextMergedProps
        // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute occ context props when cms data has loaded
    }, [logger, isPending])

    if (isPending) {
        return null
    }

    return (
        <QueryClientProvider client={queryClient}>
            <OccContextComponent {...occContextProps}>
                <Helmet>
                    <title>{sanitizeSiteTitle(cmsDataService.get().siteTitle)}</title>
                </Helmet>
                <PagesRouterElement Layout={Layout} />
            </OccContextComponent>
        </QueryClientProvider>
    )
}
